/**
 * Creates a debounced version of an async function.
 * The debounced function will delay execution until after `wait` milliseconds have elapsed
 * since the last time it was invoked.
 *
 * @param fn The async function to debounce
 * @param wait The time in milliseconds to delay execution
 * @returns A debounced version of the input async function that returns a Promise
 */
export default function debounceAsync<T extends (...args: any[]) => Promise<any>>(fn: T, wait: number): (...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>> {
	let timeoutId: ReturnType<typeof setTimeout> | null = null;
	let lastResolve: ((value: any) => void) | null = null;
	let lastReject: ((reason: any) => void) | null = null;

	return function (this: any, ...args: Parameters<T>) {
		if (timeoutId !== null) {
			clearTimeout(timeoutId);
			timeoutId = null;
			// Optionally reject the previous promise:
			// lastReject?.(new Error("Debounced by a newer call"));
		}

		return new Promise<Awaited<ReturnType<T>>>((resolve, reject) => {
			lastResolve = resolve;
			lastReject = reject;

			timeoutId = setTimeout(async () => {
				timeoutId = null;
				try {
					const result = await fn.apply(this, args);
					lastResolve?.(result);
				} catch (err) {
					lastReject?.(err);
				}
			}, wait);
		});
	};
}
